import { Fragment, forwardRef } from 'react';
import { MenuItem, MenuItems, MenuWrapper } from '../Button/MenuButton';
import { FiChevronDown } from 'react-icons/fi';
import { IconButton } from '../Button';
import { Listbox } from '@headlessui/react';
import Stack from '../Stack';
import styled from 'styled-components';
import { withMixpanelChange } from 'utils/app';

const SelectLabel = styled.span`
    color: ${({ theme }) => theme.gray_3};
    font-weight: ${({ theme }) => theme.fontSemiBold};
    &::after {
        content: ':';
    }
`;

const SelectContent = styled(Stack).attrs(
    ({ $direction = 'row', $space = (theme) => `${theme.space_3}px` }) => ({
        $direction,
        $space,
    }),
)`
    display: flex;
    flex-direction: row;
    align-items: center;
    line-height: 0.8em;
`;

const SelectButton = forwardRef(
    (
        {
            selectLabel,
            value,
            children,
            placeholder = 'Select an option',
            as = IconButton,
            IconRight = FiChevronDown,
            ...props
        },
        ref,
    ) => (
        <Listbox.Button
            as={as}
            IconRight={IconRight}
            placeholder={placeholder}
            {...props}
            ref={ref}
        >
            {children || (
                <SelectContent>
                    {selectLabel ? (
                        <SelectLabel>{selectLabel}</SelectLabel>
                    ) : null}
                    <span>
                        {value && value.label
                            ? value.label
                            : value && value.value
                              ? value.value
                              : placeholder}
                    </span>
                </SelectContent>
            )}
        </Listbox.Button>
    ),
);

const SelectOption = forwardRef(
    ({ active, selected, option, children, ...props }, ref) => (
        <MenuItem
            className={active || selected ? 'active' : ''}
            {...props}
            ref={ref}
        >
            {children || option.label || option.value}
        </MenuItem>
    ),
);

const SelectOptions = forwardRef((props, ref) => (
    <MenuItems {...props} ref={ref} />
));

const Select = withMixpanelChange(
    ({
        value,
        onChange,
        options,
        buttonProps,
        placeholder,
        isDisabled,
        isLoading,
        SelectButton: SelectButtonComponent = SelectButton,
        SelectOptions: SelectOptionsComponent = SelectOptions,
        SelectOption: SelectOptionComponent = SelectOption,
        $menuAlign,
        ...props
    }) => {
        return (
            <Listbox
                value={value}
                onChange={onChange}
                as={MenuWrapper}
                disabled={!!isDisabled}
                {...props}
            >
                <SelectButtonComponent
                    value={value}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    fetching={isLoading}
                    week={props?.week}
                    year={props?.year}
                    {...buttonProps}
                />
                <Listbox.Options
                    as={SelectOptionsComponent}
                    $menuAlign={$menuAlign}
                >
                    {options.map((option) => (
                        <Listbox.Option
                            as={Fragment}
                            value={option}
                            key={option.id || option.value}
                        >
                            {({ active, selected }) => (
                                <SelectOptionComponent
                                    active={active}
                                    selected={selected}
                                    option={option}
                                    week={props?.week}
                                    year={props?.year}
                                    {...option}
                                />
                            )}
                        </Listbox.Option>
                    ))}
                </Listbox.Options>
            </Listbox>
        );
    },
    'Select',
);

export default Select;
